import { Button } from "@hub/components/ui/button";
import type { OrgBot } from "@hub/lib/org-bots";
import { apiFetch } from "@hub/ui/api";
import { useState } from "react";

export function OrgBotList({
  slug,
  bots,
  onRemoved,
}: {
  slug: string;
  bots: OrgBot[];
  onRemoved: () => Promise<void>;
}) {
  const [target, setTarget] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  async function remove(id: string) {
    setSaving(true);
    setError(null);
    try {
      await apiFetch(`/api/orgs/${encodeURIComponent(slug)}/bots/${encodeURIComponent(id)}`, {
        method: "DELETE",
      });
      setTarget(null);
      await onRemoved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not remove bot");
    } finally {
      setSaving(false);
    }
  }
  if (bots.length === 0) return null;
  return (
    <div className="space-y-3">
      <h3 className="font-semibold">Organization bots</h3>
      {bots.map((bot) => (
        <div key={bot.id} className="space-y-3 rounded-lg border p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h4 className="font-medium">{bot.name}</h4>
              <p className="text-sm text-muted-foreground">{bot.title}</p>
              <p className="text-xs text-muted-foreground">Available to this organization</p>
            </div>
            {target !== bot.id ? (
              <Button
                type="button"
                variant="outline"
                aria-label={`Remove ${bot.name} from catalog`}
                onClick={() => {
                  setTarget(bot.id);
                  setError(null);
                }}
              >
                Remove
              </Button>
            ) : null}
          </div>
          {target === bot.id ? (
            <div className="space-y-3">
              <p className="text-sm">
                Remove {bot.name} from the organization’s catalog? People who already added it keep
                their copy.
              </p>
              {error ? (
                <p role="alert" className="text-sm text-destructive">
                  {error}
                </p>
              ) : null}
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="destructive"
                  disabled={saving}
                  onClick={() => remove(bot.id)}
                >
                  {saving ? "Removing…" : "Remove from catalog"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={saving}
                  onClick={() => setTarget(null)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}
